import React from "react";
import Modal from "react-bootstrap/Modal";
import { Col, Container, Row, Spinner } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";
import { ErrorMessage, Form, FormikProvider } from "formik";
import { MultiSelect, ReactSelect } from "@services/ui-components";

import { useMenuLanguagesForm } from "./useMenuLanguagesForm";
import {
    useGerLanguagesForMenuQuery,
    useUpdateCompanyLanguagesMutation,
} from "@me-team/host/main/Modules/graphql/modules.hooks";
import { ModuleInputType, ModuleProxy } from "@me-team/host/main/graphql/types";
import { ModulesTagName } from "../../../../../utils/enums";
import { useParams } from "react-router-dom";

type Option = {
    value: number;
    label: string;
};

type MultiOption = Option;

type LanguageModalForMenuProps = {
    isOpen: boolean;
    onClose: () => void;
    modulesList: ModuleProxy[];
    updateModules: (companyId: number, updates: ModuleInputType[], branchId: number) => Promise<void>;
    canManageModules: boolean;
};

const LanguageModalForMenu: React.FC<LanguageModalForMenuProps> = ({
    isOpen,
    onClose,
    modulesList,
    updateModules,
    canManageModules,
}) => {
    const { t } = useTranslation();
    const { currentCompanyId, branchId } = useParams();

    const { data, loading, error } = useGerLanguagesForMenuQuery({
        fetchPolicy: "network-only",
        skip: !isOpen,
    });

    const [updateCompanyLanguages] = useUpdateCompanyLanguagesMutation({
        fetchPolicy: "network-only",
    });

    const menuModule = modulesList.find((module) => module.name === ModulesTagName.Menu);

    const languages = data?.relations?.languages ?? [];

    const onSubmit = async (values: { languages: number[]; primaryLanguage: number | null }) => {
        if (!canManageModules) return;
        if (!menuModule) return;

        await updateCompanyLanguages({
            variables: {
                companyId: Number(currentCompanyId),
                branchId: Number(branchId),
                input: values,
            },
        });

        const updates: ModuleInputType[] = [{ id: menuModule.id, isActive: true }];

        await updateModules(Number(currentCompanyId), updates, Number(branchId));

        handleClose();
    };

    const {
        formik,
        languageOptions,
        primaryLanguageOptions,
        primaryLanguageValue,
        handleLanguagesChange,
        handlePrimaryLanguageChange,
    } = useMenuLanguagesForm({
        languages,
        onSubmit,
        t,
    });

    const handleClose = () => {
        formik.resetForm();
        onClose();
    };

    const selectedLanguageIds = formik.values.languages ?? [];
    const isControlsDisabled = loading || formik.isSubmitting || !!error || !canManageModules;

    return (
        <Modal show={isOpen} size="lg" onHide={handleClose} aria-labelledby="contained-modal-title-vcenter" centered>
            <Modal.Header closeButton className="border-bottom-0">
                <Modal.Title>
                    <h5 className="mb-1">{t("Menu module connection")}</h5>
                    <p className="mb-0 fs-normal text-grey">
                        {t("To connect the Menu module, select the languages in which you will fill in the menu.")}
                    </p>
                </Modal.Title>
            </Modal.Header>

            <FormikProvider value={formik}>
                <Form onSubmit={formik.handleSubmit}>
                    <Modal.Body>
                        {loading ? (
                            <div className="py-4 d-flex justify-content-center align-items-center">
                                <Spinner animation="border" size="sm" className="me-2" />
                                <span>{t("Loading...")}</span>
                            </div>
                        ) : error ? (
                            <div className="py-2 text-danger">{t("Failed to load languages")}</div>
                        ) : (
                            <>
                                <Row className="mb-4">
                                    <Col sm={3} md={2} className="text-start mb-2 mb-sm-0">
                                        <label htmlFor="editCompany-languages-field" className="required-label">
                                            <span className="fs-7 fw-bold text-start">
                                                {t("Language versions of the menu")}
                                            </span>
                                        </label>
                                    </Col>

                                    <Col sm={9} md={10}>
                                        <MultiSelect
                                            id="editCompany-languages-field"
                                            name="languages"
                                            options={languageOptions}
                                            value={languageOptions.filter((option) =>
                                                selectedLanguageIds.includes(option.value)
                                            )}
                                            onChange={(selectedOptions: MultiOption[] | null) =>
                                                handleLanguagesChange(selectedOptions)
                                            }
                                            placeholder={t("Select menu Language versions")}
                                            isSearchable
                                            disabled={isControlsDisabled}
                                        />

                                        <ErrorMessage name="languages">
                                            {(msg) => <div className="invalid-feedback d-block">{msg}</div>}
                                        </ErrorMessage>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col sm={3} md={2} className="text-start mb-2 mb-sm-0">
                                        <label htmlFor="editCompany-primaryLanguage-field" className="required-label">
                                            <span className="fs-7 fw-bold text-start">{t("Main language menu")}</span>
                                        </label>
                                    </Col>

                                    <Col sm={9} md={10}>
                                        <ReactSelect
                                            id="editCompany-primaryLanguage-field"
                                            name="primaryLanguage"
                                            value={primaryLanguageValue}
                                            options={primaryLanguageOptions}
                                            placeholder={t("Choose a language")}
                                            onChange={(option: Option | null) => handlePrimaryLanguageChange(option)}
                                            isSearchable
                                            disabled={isControlsDisabled || !primaryLanguageOptions.length}
                                        />

                                        <ErrorMessage name="primaryLanguage">
                                            {(msg) => <div className="invalid-feedback d-block">{msg}</div>}
                                        </ErrorMessage>
                                    </Col>
                                </Row>
                            </>
                        )}
                    </Modal.Body>

                    <Modal.Footer className="border-top-0">
                        <Container>
                            <Row className="justify-content-end">
                                <Col xs={3} className="px-2">
                                    <Button
                                        type="button"
                                        variant="outline-primary"
                                        className="w-100 fw-normal"
                                        onClick={handleClose}
                                        disabled={formik.isSubmitting}
                                    >
                                        {t("Cancel")}
                                    </Button>
                                </Col>

                                <Col xs={3} className="ps-2 pe-0">
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        className="w-100 fw-normal"
                                        disabled={isControlsDisabled}
                                    >
                                        {t("Save")}
                                    </Button>
                                </Col>
                            </Row>
                        </Container>
                    </Modal.Footer>
                </Form>
            </FormikProvider>
        </Modal>
    );
};

export default LanguageModalForMenu;
